'use client';
import banner1 from '@/assets/images/convention/discount/student-discount.jpg';
import banner1_m from '@/assets/images/convention/discount/student-discount_m.jpg';
import Button from '@/components/Button';
import Field from '@/components/Field';
import FormError from '@/components/FormError';
import TextInput from '@/components/TextInput';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import Upload from '@/components/Upload';
import { websiteEnv } from '@/constants/website-env';
import useIsMobile from '@/hooks/use-is-mobile';
import { isRequired } from '@/util/formValidators';
import { Form } from 'informed';
import Image from 'next/image';
import { Fragment, useEffect, useRef, useState } from 'react';
import classes from './studentDiscount.module.css';
import useStudentDiscountsPage from './useStudentDiscountsPage';

export const StudentDiscount = () => {
  const pageTitle = '12% STUDENTS DISCOUNT';

  const isMobile = useIsMobile();
  const uploadRef = useRef(null);

  const talonProps = useStudentDiscountsPage({
    uploadRef,
  });

  const { isLoading, errors, handleSubmit, response, isUploadImage, formApiRef } = talonProps;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (response && response.code === '200') {
      setOpen(true);
      setTimeout(() => {
        setOpen(false);
      }, 3000);
    }
  }, [response]);

  const maybeSuccessInfo =
    response && response.code === '200' ? (
      <span className={classes.successInfo}>
        We have received your application and will get back to you via email within 48 hours!
      </span>
    ) : response && response.code === '500' ? (
      <span className={classes.msgerror}>{response.content}</span>
    ) : null;

  const buttonText = isLoading ? 'Loading...' : ' Verify Now';

  return (
    <Fragment>
      <article className={classes.root}>
        <div className={classes.studentdiscount}>
          <div className={classes.topcontent}>
            <Image
              className={classes.illustration}
              src={isMobile ? banner1_m : banner1}
              alt=''
            />
            <div className={classes.formcon}>
              <h1 className={classes.title}>{pageTitle}</h1>
              <h5 className={classes.subtitle}>Unlock 12% off student discount at awbridal.com!</h5>
              <FormError
                allowErrorMessages
                errors={Array.from(errors.values())}
              />
              <Form
                className={classes.form}
                onSubmit={handleSubmit}
                formApiRef={formApiRef}
                suppressHydrationWarning
              >
                <Field
                  id='first-name'
                  label={<span className='text-[15px]'>First Name</span>}
                  required
                >
                  <TextInput
                    autoComplete='firstname'
                    field='firstname'
                    id='firstName'
                    validate={isRequired}
                    suppressHydrationWarning
                  />
                </Field>
                <Field
                  id='last-name'
                  label={<span className='text-[15px]'>Last Name</span>}
                  required
                >
                  <TextInput
                    autoComplete='lastname'
                    field='lastname'
                    id='lastName'
                    validate={isRequired}
                    suppressHydrationWarning
                  />
                </Field>
                <Field
                  id='email'
                  label={<span className='text-[15px]'>Email</span>}
                  required
                  classes={{
                    root: classes.email,
                  }}
                >
                  <TextInput
                    autoComplete='email'
                    field='email'
                    id='Email'
                    validate={isRequired}
                    suppressHydrationWarning
                  />
                </Field>
                <Field
                  id='photo'
                  label={<span className='text-[15px]'>Photo of your Student ID</span>}
                  required
                  classes={{
                    root: classes.photo,
                  }}
                >
                  <Upload
                    ref={uploadRef}
                    showUploadList={{
                      showDownloadIcon: false,
                    }}
                    progress={{
                      strokeWidth: 0,
                    }}
                  />
                  <div className={classes.picadd}>
                    <span>1 image, jpg/png only.</span>
                  </div>
                  {isUploadImage ? <p className={classes.msgerror}>Photo Cannot Be Empty.</p> : null}
                </Field>
                <div className={classes.primary}>
                  <Button
                    type='submit'
                    className={classes.submitButton}
                  >
                    {buttonText}
                  </Button>
                </div>
                {maybeSuccessInfo}
              </Form>
            </div>
          </div>
          <div className={classes.bottomconent}>
            <div className={classes.frequentlyqa}>
              <h1 className={classes.bottomconenttitle}>Frequently Asked Questions</h1>
              <div className={classes.qaitem}>
                <p className={classes.q}>Who is eligible for this discount?</p>
                <p className={classes.a}>
                  Students who are currently enrolled at a high school, college or university are eligible for the
                  discount.
                </p>
              </div>
              <div className={classes.qaitem}>
                <p className={classes.q}>How to verify my identity?</p>
                <p className={classes.a}>
                  Verification is quick and simple! Just submit your information to complete your verification.
                  Sometimes additional documentation is required. Just follow the page guides and it won't take long.
                </p>
              </div>
              <div className={classes.qaitem}>
                <p className={classes.q}>Is the student discount eligible for past orders?</p>
                <p className={classes.a}>
                  Unfortunately, this discount offer can not be applied to orders previously placed on awbridal.com or
                  on other sales channels like Amazon, eBay or Walmart.
                </p>
              </div>
            </div>
            <div className={classes.termsconditions}>
              <h1 className={classes.bottomconenttitle}>Students Discount Terms and Conditions</h1>
              <ul className={classes.termsconditionsdateil}>
                <li>The discount {websiteEnv.isUnitedKingdom ? 'is' : 'takes'} 12% off your order.</li>
                <li>Only one discount code can be used per order.</li>
                <li>
                  The coupon is valid for 1 month, and only one coupon code can be issued per valid Student ID within a
                  3-month period.
                </li>
                <li>
                  The coupon code must be used for an order placed under your name and email address. Orders placed
                  under different names may be {websiteEnv.isUnitedKingdom ? 'cancelled' : 'canceled'} without notice,
                  and no additional codes will be offered.
                </li>
                <li>Previous orders are not eligible for reimbursement.</li>
                <li>By entering you confirm that you have read and agree to these terms and conditions.</li>
                <li>We do not collect or store your information; it is used solely for verification purposes.</li>
                <li>
                  If you have any issues obtaining the code or have questions, please contact us at{' '}
                  <a
                    href='mailto:service@cs.awbridal.com'
                    className='underline lg_hover_text-80583F'
                  >
                    service@cs.awbridal.com
                  </a>
                  .
                </li>
              </ul>
            </div>
          </div>
        </div>
      </article>

      <Dialog
        open={open}
        onOpenChange={setOpen}
      >
        <DialogContent
          className='border-none bg-transparent shadow-none sm_max-w-[425px]'
          showCloseButton={false}
          onInteractOutside={e => {
            e.preventDefault();
          }}
        >
          <div className='flex h-full w-full flex-col items-center justify-center gap-[18px] text-center'>
            <Image
              src={`${process.env.NEXT_PUBLIC_BASE_PATH}/product-full-detail/product-full-detail-mask-48.webp?v=1.2.3`}
              alt='Product Full Detail Mask'
              width={60}
              height={60}
              className='text-center'
            />
            <p className='max-w-[338px] text-center text-white'>
              We have received your application and will get back to you via email within 48 hours!
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default StudentDiscount;
